import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import MyTasksControlsBar from './MyTasksControlsBar';
import TaskListTable from '@/components/ProjectManagement/TaskListTable';
import KanbanBoard from '@/components/ProjectManagement/kanban';
import TimelineBoard from '@/components/ProjectManagement/timeline';
import SprintBoard from '@/components/ProjectManagement/sprint';
import { fetchTasks } from '@/components/ProjectManagement/api/projectManagementApi';

dayjs.extend(isBetween);

export default function MyTasksWorkspace({
    tasks = [],
    loading: parentLoading = false,
    selectedStatus = null,
    onStatusSelect,
    onClearStatusFilter,
    selectedProject = null,
    onProjectSelect,
    selectedSprint = null,
    onSprintSelect,
    selectedMilestone = null,
    onMilestoneSelect,
    onTaskClick,
    onStatusChange,
    onEdit,
    onDelete,
    onConvertType,
    onNewTask,
}) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const currentAdminId = currentAdmin?._id || currentAdmin?.id;

    const [activeTab, setActiveTab] = useState('list');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 10;

    // Search dropdown state (Backend Spotlight Quick-Find)
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);

    // Local filters
    const [localProject, setLocalProject] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [dueDateFilter, setDueDateFilter] = useState(null);
    const [sprintFilter, setSprintFilter] = useState(null);
    const [milestoneFilter, setMilestoneFilter] = useState(null);

    const activeProject = selectedProject !== undefined && selectedProject !== null ? selectedProject : localProject;
    const activeSprint = selectedSprint !== undefined && selectedSprint !== null ? selectedSprint : sprintFilter;
    const activeMilestone = selectedMilestone !== undefined && selectedMilestone !== null ? selectedMilestone : milestoneFilter;

    // ── 1. Backend Paginated Table Tasks ──────────────────────────────────────
    const [tableTasks, setTableTasks] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [tablePagination, setTablePagination] = useState({
        page: 1,
        limit: pageSize,
        total: 0,
        totalPages: 1,
    });

    // ── 2. Backend Board Tasks (for Kanban / Timeline / Sprint) ───────────────
    const [boardTasks, setBoardTasks] = useState([]);
    const [boardLoading, setBoardLoading] = useState(false);

    // ── 3. Debounce Search Input (300ms) ──────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // ── 4. Backend-Driven Spotlight Quick-Find Search ─────────────────────────
    useEffect(() => {
        const q = debouncedSearchQuery.trim();
        if (!q) {
            setSearchResults([]);
            setSearchLoading(false);
            setSearchDropdownOpen(false);
            return;
        }

        let isMounted = true;
        setSearchLoading(true);
        setSearchDropdownOpen(true);

        const params = {
            search: q,
            limit: 8,
            itemType: 'task',
        };
        if (currentAdminId) params.assignee = currentAdminId;
        if (activeProject) params.projectId = activeProject;

        fetchTasks(params)
            .then(({ data }) => {
                if (isMounted) {
                    setSearchResults(data?.result || []);
                }
            })
            .catch(() => {
                if (isMounted) setSearchResults([]);
            })
            .finally(() => {
                if (isMounted) setSearchLoading(false);
            });

        return () => { isMounted = false; };
    }, [debouncedSearchQuery, currentAdminId, activeProject]);

    // ── 5. Server-Side Paginated Fetch for Table View ─────────────────────────
    const loadTableTasks = useCallback(async (targetPage = 1) => {
        if (!currentAdminId) return;
        setTableLoading(true);
        try {
            const params = {
                page: targetPage,
                limit: pageSize,
                itemType: 'task',
                excludeMilestones: true,
                assignee: currentAdminId,
            };
            if (activeProject) params.projectId = activeProject;
            if (selectedStatus) params.status = selectedStatus;
            if (priorityFilter) params.priority = priorityFilter;
            if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();

            const { data } = await fetchTasks(params);
            setTableTasks(data?.result || []);
            if (data?.pagination) {
                setTablePagination({
                    page: data.pagination.page || targetPage,
                    limit: data.pagination.limit || pageSize,
                    total: data.pagination.total || 0,
                    totalPages: data.pagination.totalPages || 1,
                });
            }
        } catch (err) {
            console.error('[MyWork] Failed to fetch server-paginated table tasks:', err);
            setTableTasks([]);
        } finally {
            setTableLoading(false);
        }
    }, [currentAdminId, activeProject, selectedStatus, priorityFilter, debouncedSearchQuery, pageSize]);

    // ── 6. Fetch Full Task Set for Kanban / Timeline / Sprint Boards ──────────
    const loadBoardTasks = useCallback(async () => {
        if (!currentAdminId) return;
        setBoardLoading(true);
        try {
            const params = {
                limit: 'all',
                itemType: 'task',
                excludeMilestones: true,
                assignee: currentAdminId,
            };
            if (activeProject) params.projectId = activeProject;
            if (selectedStatus) params.status = selectedStatus;
            if (priorityFilter) params.priority = priorityFilter;
            if (debouncedSearchQuery.trim()) params.search = debouncedSearchQuery.trim();

            const { data } = await fetchTasks(params);
            setBoardTasks(data?.result || []);
        } catch (err) {
            console.error('[MyWork] Failed to fetch board tasks:', err);
            setBoardTasks([]);
        } finally {
            setBoardLoading(false);
        }
    }, [currentAdminId, activeProject, selectedStatus, priorityFilter, debouncedSearchQuery]);

    // Trigger table load when page or filters change
    useEffect(() => {
        loadTableTasks(page);
    }, [loadTableTasks, page]);

    // Trigger board tasks load when non-list tab is active
    useEffect(() => {
        if (activeTab !== 'list') {
            loadBoardTasks();
        }
    }, [activeTab, loadBoardTasks]);

    // Reset page to 1 whenever any filter changes
    useEffect(() => {
        setPage(1);
    }, [selectedStatus, activeProject, priorityFilter, dueDateFilter, activeSprint, activeMilestone]);

    // Derive dropdown options dynamically from user tasks
    const availableProjects = useMemo(() => {
        const map = new Map();
        (boardTasks.length > 0 ? boardTasks : tasks).forEach((t) => {
            const p = t.project;
            if (p && (p._id || p.id)) {
                const id = String(p._id || p.id);
                if (!map.has(id)) {
                    map.set(id, { id, title: p.title || p.name || 'Project' });
                }
            }
        });
        return Array.from(map.values());
    }, [boardTasks, tasks]);

    const availableSprints = useMemo(() => {
        const set = new Set();
        (boardTasks.length > 0 ? boardTasks : tasks).forEach((t) => {
            const sName = t.sprint?.name || (typeof t.sprint === 'string' ? t.sprint : null);
            if (sName) set.add(sName);
        });
        return Array.from(set);
    }, [boardTasks, tasks]);

    const availableMilestones = useMemo(() => {
        const map = new Map();
        (boardTasks.length > 0 ? boardTasks : tasks).forEach((t) => {
            const m = t.milestoneId;
            if (m && (m._id || m.id || typeof m === 'string')) {
                const id = String(m._id || m.id || m);
                const title = m.title || m.name || 'Milestone';
                if (!map.has(id)) {
                    map.set(id, { id, title });
                }
            }
        });
        return Array.from(map.values());
    }, [boardTasks, tasks]);

    const handleRowClick = (taskOrId) => {
        if (!taskOrId) return;
        const id = typeof taskOrId === 'string' ? taskOrId : (taskOrId._id || taskOrId.id);
        if (id && onTaskClick) {
            onTaskClick(id);
        }
    };

    const hasActiveFilters = Boolean(
        selectedStatus ||
        activeProject ||
        priorityFilter ||
        dueDateFilter ||
        activeSprint ||
        activeMilestone ||
        searchQuery
    );

    const handleResetAllFilters = () => {
        setSearchQuery('');
        setDebouncedSearchQuery('');
        setPage(1);
        if (onClearStatusFilter) onClearStatusFilter();
        if (onProjectSelect) onProjectSelect(null);
        if (onSprintSelect) onSprintSelect(null);
        if (onMilestoneSelect) onMilestoneSelect(null);
        setLocalProject(null);
        setPriorityFilter(null);
        setDueDateFilter(null);
        setSprintFilter(null);
        setMilestoneFilter(null);
    };

    const handleProjectChange = (val) => {
        setLocalProject(val || null);
        if (onProjectSelect) onProjectSelect(val || null);
        setPage(1);
    };

    const handleStatusUpdate = async (taskId, newStatus) => {
        if (onStatusChange) {
            await onStatusChange(taskId, newStatus);
            loadTableTasks(page);
            if (activeTab !== 'list') loadBoardTasks();
        }
    };

    return (
        <div className="space-y-4">
            {/* 1. Sleek Controls Bar with Backend Spotlight Search */}
            <MyTasksControlsBar
                activeTab={activeTab}
                onTabChange={setActiveTab}
                searchQuery={searchQuery}
                onSearchChange={(q) => setSearchQuery(q)}
                searchResults={searchResults}
                searchLoading={searchLoading}
                searchDropdownOpen={searchDropdownOpen}
                setSearchDropdownOpen={setSearchDropdownOpen}
                onTaskClick={handleRowClick}
                selectedStatus={selectedStatus}
                onStatusChange={(status) => {
                    if (onStatusSelect) onStatusSelect(status || null);
                    setPage(1);
                }}
                selectedProject={activeProject}
                onProjectChange={handleProjectChange}
                selectedPriority={priorityFilter}
                onPriorityChange={(p) => {
                    setPriorityFilter(p || null);
                    setPage(1);
                }}
                selectedDueDate={dueDateFilter}
                onDueDateChange={(d) => {
                    setDueDateFilter(d || null);
                    setPage(1);
                }}
                selectedSprint={activeSprint}
                onSprintChange={(s) => {
                    if (onSprintSelect) onSprintSelect(s || null);
                    setSprintFilter(s || null);
                    setPage(1);
                }}
                selectedMilestone={activeMilestone}
                onMilestoneChange={(m) => {
                    if (onMilestoneSelect) onMilestoneSelect(m || null);
                    setMilestoneFilter(m || null);
                    setPage(1);
                }}
                onResetFilters={handleResetAllFilters}
                availableProjects={availableProjects}
                availableSprints={availableSprints}
                availableMilestones={availableMilestones}
                onNewTask={onNewTask}
            />

            {/* 2. Views Container aligned with main task page parity */}
            <div>
                {activeTab === 'list' && (
                    <TaskListTable
                        tasks={tableTasks}
                        loading={tableLoading || parentLoading}
                        pagination={tablePagination}
                        onPageChange={(newPage) => setPage(newPage)}
                        onRowClick={handleRowClick}
                        onProjectClick={(projId) => {
                            if (onProjectSelect) onProjectSelect(projId);
                            else setLocalProject(projId);
                        }}
                        onStatusChange={handleStatusUpdate}
                        onEdit={onEdit}
                        onDelete={async (id) => {
                            if (onDelete) await onDelete(id);
                            loadTableTasks(page);
                        }}
                        onConvertType={onConvertType}
                        onAddSubtask={(task) => onNewTask?.('Backlog', 'task')}
                        searchQuery={searchQuery}
                        onClearSearch={() => {
                            setSearchQuery('');
                            setPage(1);
                        }}
                        hasActiveFilters={hasActiveFilters}
                        onResetFilters={handleResetAllFilters}
                        projects={availableProjects}
                    />
                )}

                {activeTab === 'kanban' && (
                    <KanbanBoard
                        tasks={boardTasks}
                        loading={boardLoading}
                        onRowClick={handleRowClick}
                        onCardClick={handleRowClick}
                        onStatusChange={handleStatusUpdate}
                        onAddTask={(status) => onNewTask?.(status || 'Backlog', 'task')}
                        onEdit={onEdit}
                        onDelete={async (id) => {
                            if (onDelete) await onDelete(id);
                            loadBoardTasks();
                        }}
                    />
                )}

                {activeTab === 'timeline' && (
                    <TimelineBoard
                        tasks={boardTasks}
                        loading={boardLoading}
                        onRowClick={handleRowClick}
                        onTaskClick={handleRowClick}
                    />
                )}

                {activeTab === 'sprint' && (
                    <SprintBoard
                        tasks={boardTasks}
                        loading={boardLoading}
                        onRowClick={handleRowClick}
                        onTaskClick={handleRowClick}
                        onStatusChange={handleStatusUpdate}
                    />
                )}
            </div>
        </div>
    );
}
